import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, RefreshCw } from "lucide-react";
import { TIPOS_MODALIDAD, VEHICULOS } from "@/lib/constants";

export default function CambiarModalidadDialog({ open, onOpenChange, reserva, onChanged }) {
  const [modalidad, setModalidad] = useState("regular");
  const [vehiculo, setVehiculo] = useState("bus");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && reserva) {
      setModalidad(reserva.modalidad || "regular");
      setVehiculo(reserva.vehiculo || (reserva.modalidad === "vip" ? "van" : "bus"));
    }
  }, [open, reserva]);

  const guardar = async () => {
    setSaving(true);
    try {
      await api.post(`/reservas/${reserva.id}/modalidad`, { modalidad, vehiculo });
      toast.success("Modalidad reasignada. Se recalculó el cupo de las salidas afectadas.");
      onChanged?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="cambiar-modalidad-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <RefreshCw className="h-5 w-5" /> Cambiar modalidad
          </DialogTitle>
          <DialogDescription>
            Reasigná la reserva (ej. VIP → Regular). La reserva original se conserva; se mueve a la
            salida correspondiente y se recalcula el cupo.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Modalidad</Label>
            <Select value={modalidad} onValueChange={setModalidad}>
              <SelectTrigger data-testid="modalidad-nueva-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(TIPOS_MODALIDAD).map(([k, v]) => (
                  <SelectItem key={k} value={k}>
                    {v}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Vehículo</Label>
            <Select value={vehiculo} onValueChange={setVehiculo}>
              <SelectTrigger data-testid="vehiculo-nuevo-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(VEHICULOS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>
                    {v}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={guardar} disabled={saving} data-testid="cambiar-modalidad-confirm">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reasignar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
